<template>
  <section
    class="file-container"
    :class="{ selected: selected, analyzing: analyzing }"
  >
    <template v-if="analyzing">
      <rotating-spinner></rotating-spinner>
    </template>
    <template v-else>
      <select-box
        class="select-box"
        @action="$emit('select-file', file, 'multi')"
        :selected="selected"
      ></select-box>
    </template>
    <span
      class="filename"
      :data-filename-start="fileNameStart"
      :data-filename-end="fileNameEnd"
      :data-extension="fileExtension"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @mousemove="handleMouseMove"
    ></span>
    <teleport to="body">
      <span
        v-if="showTooltip"
        class="filename-tooltip"
        :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
      >
        {{ file.fileName }}
      </span>
    </teleport>
    <button class="remove-file" @click.stop="$emit('remove', file)">
      <svg class="trash" width="15px" height="15px" viewBox="0 0 15 15">
        <title>Verwijderen</title>
        <g id="prullenmand">
          <path
            fill="#4f4f4f"
            d="M3,4 L3,13.25 C3,13.6642136 3.3581722,14 3.8,14 L10.2,14 C10.6418278,14 11,13.6642136 11,13.25 L11,4 L3,4 Z M9,2 L13,2 L13,3 L12,3 L12,13.4285714 C12,14.2964475 11.2538079,15 10.3333333,15 L3.66666667,15 C2.74619208,15 2,14.2964475 2,13.4285714 L2,3 L1,3 L1,2 L5,2 L5,1 C5,0.44771525 5.44771525,1.01453063e-16 6,0 L8,0 C8.55228475,-1.01453063e-16 9,0.44771525 9,1 L9,2 Z M5,6 L5,12 L6,12 L6,6 L5,6 Z M8,6 L8,12 L9,12 L9,6 L8,6 Z"
          ></path>
        </g>
      </svg>
    </button>

    <div class="product">
      <span class="italic analyse-text" v-if="analyzing"
        >Bezig met bestandsanalyse...</span
      >

      <span class="italic" v-else-if="!productSet">Kies een product</span>
      <span class="prod-title" v-else-if="productSet">
        <product-quantity
          v-if="selected && quantityEditable"
          :quantity="file.quantity || 1"
          :editable="true"
          :plain="true"
          :min-quantity="product?.minQuantity"
          @setQuantity="$emit('update-quantity', $event)"
          @click.stop
          @mousedown.stop
        ></product-quantity>
        <span v-else-if="file.quantity" class="quantity"
          >{{ file.quantity }}x</span
        >
        {{ file.productSpecs.title }}
      </span>

      <span
        v-else-if="lowestFileDpi < 197"
        @click="showDpiInfo"
        class="dpi-warning"
        >DPI waarschuwing!</span
      >
    </div>

    <div v-if="productSet && showCartButton" class="cart-area">
      <span v-if="requiredSpecsMissing" class="required-warning error-text">
        Niet alle verplichte velden zijn ingevuld
      </span>
      <button v-else class="cart-button" @click.stop="addToCart">
        <inline-svg src="/images/cart-button/plus.svg" />In winkelwagen
      </button>
    </div>

    <div class="specs-row" v-if="productSet && !showCartButton">
      <span v-if="requiredSpecsMissing" class="required-warning error-text">
        Niet alle verplichte velden zijn ingevuld
      </span>
      <template v-else>
        <span
          v-for="(spec, index) in visibleSpecs"
          :key="index"
          class="spec-tag"
          >{{ spec.optionTitle }}</span
        >
      </template>
    </div>

    <template v-if="analyseFiles">
      <analyse-trigger
        v-if="!analyzing"
        @click.native="toggleAnalyse"
        class="analyse-btn"
        :open="showAnalyse"
        :status="file.status"
      ></analyse-trigger>

      <transition-height>
        <analyse-info
          class="analyse-info"
          :class="{
            open: showAnalyse
          }"
          :file="file"
          v-if="showAnalyse"
        ></analyse-info>
      </transition-height>
    </template>
  </section>
</template>

<script>
import CartButton from '../../../../components/ui/CartButton.vue'
import AnalyseInfo from './AnalyseInfo.vue'
import AnalyseTrigger from './AnalyseTrigger.vue'
import DpiWarning from '@/dialogs/DpiWarning.vue'
import TransitionHeight from '../../../../components/TransitionHeight.vue'
import ProductQuantity from '@/components/ui/ProductQuantity.vue'
import { useProductOrderStore } from '@/features/productOrder/ProductOrder.store'
import ProductOrderMixin from '@/features/productOrder/ProductOrderMixin'

export default {
  props: ['file', 'selected', 'showCartButton'],
  mixins: [ProductOrderMixin],
  emits: ['select-file', 'remove', 'add-to-cart', 'update-quantity'],
  components: {
    AnalyseTrigger,
    AnalyseInfo,
    CartButton,
    TransitionHeight,
    ProductQuantity
  },
  data() {
    return {
      showAnalyse: false,
      showTooltip: false,
      tooltipX: 0,
      tooltipY: 0
    }
  },
  methods: {
    handleMouseEnter(event) {
      this.showTooltip = true
      this.updateTooltipPosition(event)
    },
    handleMouseLeave() {
      this.showTooltip = false
    },
    handleMouseMove(event) {
      if (this.showTooltip) {
        this.updateTooltipPosition(event)
      }
    },
    updateTooltipPosition(event) {
      this.tooltipX = event.clientX + 10
      this.tooltipY = event.clientY + 10
    },
    toggleAnalyse(event) {
      event.stopPropagation()

      this.showAnalyse = !this.showAnalyse
    },
    showDpiInfo() {
      this.$modal.show(
        DpiWarning,
        {
          name: this.file.fileName,
          dpis: this.file.dpisPerPage
        },
        {
          width: 400,

          adapive: true
        }
      )
    },
    addToCart() {
      this.$emit('add-to-cart')
    }
  },
  computed: {
    fileNameWithoutExtension() {
      const fileName = this.file.fileName
      const lastDotIndex = fileName.lastIndexOf('.')
      if (lastDotIndex === -1) return fileName
      return fileName.substring(0, lastDotIndex)
    },
    fileNameStart() {
      const nameWithoutExt = this.fileNameWithoutExtension
      const endLength = Math.min(8, Math.ceil(nameWithoutExt.length / 2))
      return nameWithoutExt.slice(0, nameWithoutExt.length - endLength)
    },
    fileNameEnd() {
      const nameWithoutExt = this.fileNameWithoutExtension
      const endLength = Math.min(8, Math.ceil(nameWithoutExt.length / 2))
      return nameWithoutExt.slice(-endLength)
    },
    fileExtension() {
      const fileName = this.file.fileName
      const lastDotIndex = fileName.lastIndexOf('.')
      if (lastDotIndex === -1) return ''
      return fileName.substring(lastDotIndex)
    },
    analyseFiles() {
      return this.rootStore.theme.customFields.analyseFiles
    },
    analyzing() {
      //return true
      return this.file.status === 'ANALYZING' ? true : false
    },
    productSet() {
      return this.file.productSpecs && this.file.productSpecs.title
        ? true
        : false
    },
    // Products that derive their quantity from the file analysis (e.g. badges from
    // the number of Excel rows) must not be adjustable here either
    product() {
      return useProductOrderStore().products?.find(
        (p) => p.title === this.file.productSpecs?.title
      )
    },
    quantityEditable() {
      return !this.hasQuantitySource(this.product)
    },
    visibleSpecs() {
      if (
        !this.file.productSpecs?.specs ||
        !Array.isArray(this.file.productSpecs.specs)
      )
        return []
      return this.file.productSpecs.specs.filter(
        (spec) =>
          spec.optionName !== 'NONE' &&
          spec.optionTitle &&
          !spec.hidden &&
          spec.title !== 'Opmerking'
      )
    },
    requiredSpecsMissing() {
      const specs = this.file.productSpecs?.specs
      if (!specs?.length) return false
      const product = useProductOrderStore().products?.find(
        (p) => p.title === this.file.productSpecs?.title
      )
      if (!product) return false
      for (const menu of product.menus) {
        for (const og of menu.optionGroups || []) {
          if (!og.required) continue
          const spec = specs.find((s) => s.optionGroupName === og.name)
          if (!spec || !spec.optionTitle) return true
        }
      }
      return false
    },
    lowestFileDpi() {
      if (!this.analyseFiles) {
        return 300
      }
      if (this.file.dpisPerPage && this.file.dpisPerPage.length > 0) {
        let lowestPageDpis = []
        this.file.dpisPerPage.forEach((page) => {
          const lowestPageDpi = page.dpis.reduce((a, b) => Math.min(a, b))
          lowestPageDpis.push(lowestPageDpi)
        })

        let lowestFileDpi = lowestPageDpis.reduce((a, b) => Math.min(a, b))

        return lowestFileDpi
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../../../sass/base/_colors.scss' as *;

.file-container {
  display: grid;
  grid-template-columns: 25px auto 29px 25px;
  grid-template-rows: 27px minmax(22px, auto) auto auto auto auto;
  grid-template-areas: 'select filename filename remove' '. product analyse-btn analyse-btn' '. cart cart cart' '. specs specs specs' '. analyse analyse analyse';
  border-radius: 20px;
  background-color: transparent;
  margin-bottom: 10px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.5s;

  .selected-row {
    grid-row: 2; /* target the second row (index starts at 1) */
    margin-top: 30px; /* add a margin to the top of the selected row to increase the gap */
    margin-bottom: 10px; /* add a margin to the bottom of the selected row to decrease the gap with the following row */
  }

  &:hover {
    box-shadow: var(--default-shadow);
  }

  &.selected {
    background-color: var(--selected-color);
    box-shadow: var(--default-shadow);
  }

  &.analyzing {
    opacity: 0.6;
    cursor: auto;
  }
}

@container (width: 274px) {
  .file-container {
    grid-template-columns: 25px 165px 29px 25px;
  }
}

.error-text {
  font-size: 12px;
}

.select-box {
  margin-top: 0;
}

.product {
  grid-area: product;
  font-weight: 500;
  padding-top: 0px;
}

.analyse-btn {
  grid-area: analyse-btn;
  color: #4f4f4f;
  align-self: center;
  justify-self: end;
}

.analyse-info {
  grid-area: analyse;
}

.analyse-text {
  text-wrap: nowrap;
  //  position: absolute;
}

.filename {
  font-weight: 600;
  grid-area: filename;
  padding-bottom: 5px;
  cursor: pointer;
  display: flex;
  overflow: hidden;
  white-space: nowrap;

  &::before {
    content: attr(data-filename-start);
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 1;
    min-width: 0;
  }

  &::after {
    content: attr(data-filename-end) attr(data-extension);
    flex-shrink: 0;
  }
}

.filename-tooltip {
  position: fixed;
  padding: 8px 12px;
  background-color: #333;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  animation: tooltipFadeIn 0.15s ease-in-out;
}

.remove-file {
  justify-self: end;
  padding-bottom: 5px;
}

.dpi-warning {
  padding-top: 5px;
  //font-weight: 600;
  //font-style: italic;
  color: #ff8f00;
}

.trash {
  &:hover {
    path {
      transition: fill 0.4s;
      fill: $rijnja-red;
    }
  }
}

.cart-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.required-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--error-color);
  color: white;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.cart-area {
  grid-area: cart;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 6px 0 0px;
}

.required-warning {
  font-size: 0.8rem;
  font-weight: 600;
}

.cart-button {
  background: var(--arch-color);
  color: var(--arch-text-color);
  padding: 5px 10px;
  border-radius: 50px;
  svg {
    margin-right: 4px;

    :deep(g) {
      fill: var(--arch-text-color);
    }
  }
}

.prod-title {
  color: var(--sub-color);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.quantity {
  font-size: 13px;
  font-weight: 600;
  color: var(--sub-color);
}

.specs-row {
  grid-area: specs;
  display: flex;
  flex-wrap: nowrap;
  overflow: hidden;
  gap: 4px;
  padding-top: 7px;
  mask-image: linear-gradient(
    to right,
    black calc(100% - 24px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    black calc(100% - 24px),
    transparent 100%
  );
  transition: mask-image 0.2s, -webkit-mask-image 0.2s;

  &:hover {
    flex-wrap: wrap;
    overflow: visible;
    mask-image: none;
    -webkit-mask-image: none;
  }
}

.spec-tag {
  font-size: 12px;
  font-weight: 400;
  color: #888;
  white-space: nowrap;
  flex-shrink: 0;

  &:not(:last-child)::after {
    content: '·';
    margin-left: 4px;
  }
}
</style>
